import { useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CalendarToolbar, type CalendarView } from "@/components/calendar/CalendarToolbar";
import { isoDate, isoMonth, isoWeek, isoYear, parseIsoYear, addYears } from "@/lib/calendar-utils";

/**
 * Lightweight year overview — 12 mini-month grids. Tap a month to drill in.
 * Density signal is intentionally minimal in v1; richer overlay deferred.
 */

const VIEW: CalendarView = "year";

export function CalendarYear() {
  const navigate = useNavigate();
  const { year } = useParams<{ year: string }>();
  const yearStart = year ? parseIsoYear(year) : new Date(new Date().getFullYear(), 0, 1);
  const y = yearStart.getFullYear();

  const months = useMemo(
    () => Array.from({ length: 12 }, (_, i) => new Date(y, i, 1)),
    [y],
  );

  function go(delta: number) {
    navigate(`/calendar/year/${isoYear(addYears(yearStart, delta))}`);
  }

  return (
    <article aria-labelledby="calendar-year-heading">
      <p
        style={{
          fontFamily: "var(--font-sans)",
          fontSize: "var(--type-micro)",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "var(--fg-3)",
          margin: 0,
        }}
      >
        Calendar · Year
      </p>
      <CalendarToolbar
        title={String(y)}
        view={VIEW}
        isoDay={isoDate(new Date())}
        isoWeek={isoWeek(new Date(y, 0, 1))}
        isoMonth={isoMonth(new Date(y, 0, 1))}
        isoYear={isoYear(yearStart)}
        onPrev={() => go(-1)}
        onNext={() => go(1)}
        onToday={() => navigate(`/calendar/year/${isoYear(new Date())}`)}
      />
      <h2 id="calendar-year-heading" style={{ position: "absolute", left: -9999 }}>
        Calendar year
      </h2>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
          gap: "var(--space-3)",
        }}
      >
        {months.map((m) => (
          <MiniMonth key={m.getMonth()} month={m} />
        ))}
      </div>
    </article>
  );
}

function MiniMonth({ month }: { month: Date }) {
  const today = new Date();
  const isCurrent = month.getMonth() === today.getMonth() && month.getFullYear() === today.getFullYear();
  const monthName = month.toLocaleString("default", { month: "long" });
  const first = new Date(month);
  const offset = (first.getDay() + 6) % 7;
  const cells: Array<{ date: Date | null }> = Array.from({ length: 42 }, () => ({ date: null }));
  for (let i = 0; i < 31; i++) {
    const d = new Date(first.getFullYear(), first.getMonth(), i + 1);
    if (d.getMonth() !== first.getMonth()) break;
    cells[offset + i] = { date: d };
  }
  return (
    <Link
      to={`/calendar/month/${isoMonth(month)}`}
      style={{
        display: "block",
        padding: "var(--space-3)",
        background: "var(--bg-2)",
        border: `1px solid ${isCurrent ? "var(--accent-dim)" : "var(--hair)"}`,
        borderRadius: "var(--radius-card)",
        textDecoration: "none",
        color: "var(--fg)",
      }}
    >
      <header
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          marginBottom: 6,
        }}
      >
        <span style={{ fontWeight: 500, fontSize: "var(--type-body-s)" }}>{monthName}</span>
        <span className="mono" style={{ color: "var(--fg-3)", fontSize: "var(--type-meta)" }}>
          {month.getFullYear()}
        </span>
      </header>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 }}>
        {cells.map((c, i) => {
          const isToday = c.date && isSameDay(c.date, today);
          return (
            <span
              key={i}
              className="mono"
              style={{
                fontSize: 9,
                textAlign: "center",
                color: c.date ? (isToday ? "var(--accent)" : "var(--fg-2)") : "transparent",
                padding: 1,
                borderRadius: 2,
                background: isToday ? "var(--accent-wash)" : "transparent",
              }}
            >
              {c.date ? c.date.getDate() : "·"}
            </span>
          );
        })}
      </div>
    </Link>
  );
}

function isSameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
